import React from 'react'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLink, faLinkSlash } from '@fortawesome/free-solid-svg-icons'
import * as state from '../../states'

import {
	IOfferScope,
	EOfferScopeStatus,
	ERoleAccess,
	OFFER_SCOPE_STATUS_LABEL_MAP,
	EOfferScopeCapType,
	OFFER_SCOPE_CAP_TYPE_LABEL_MAP
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconDeleteBin,
	IconEdit,
	CapTypeIcon,
	UiMoreTextInfo,
	IconView,
	headCellFormatterSortable,
	ESortOrder,
	NotesIconBtn
} from '../ui'
import { STATUS_COLORS_MAP } from '../../constants'
import { useAtomValue } from 'jotai'
import { OfferScopeNotesField, IPopUpPosition } from './OfferScopeNotesField'
import { OFFER_SCOPE_PAGE_SORT_FIELDS, IOfferScopeSortField } from '../../hooks'
import { Access } from '../../lib'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'


type IOfferScopeListTableProps = {
	items: IOfferScope[];
	skip: number;
	sortBy: IOfferScopeSortField;
	setSortBy: (sortBy: IOfferScopeSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onView?: (item: IOfferScope, e: React.MouseEvent) => void;
	onBind?: (item: IOfferScope, e: React.MouseEvent) => void;
	onUnbind?: (item: IOfferScope, e: React.MouseEvent) => void;
	onEdit?: (item: IOfferScope, e: React.MouseEvent) => void;
	onEditNotes?: (item: IOfferScope, e: React.MouseEvent) => void;
	onRemove?: (item: IOfferScope, e: React.MouseEvent) => void;
}

type IOfferScopeStatusProps = {
	value: EOfferScopeStatus;
	rowData: IOfferScope; // { }
	onClick?: (rowData: IOfferScope, e: React.MouseEvent) => void;
	classNamesToAdd?: string;
}

const OfferScopeStatusField = (props: IOfferScopeStatusProps) => {
	const { t } = useTranslation()
	const { value, classNamesToAdd, rowData, onClick } = props

	const keyTrans = OFFER_SCOPE_STATUS_LABEL_MAP?.[value]
	const labelTrans = t(keyTrans)

	return <>
		<span
			/*style={{cursor: 'pointer'}}*/
			className={`badge text-white fw-normal py-1 px-2 fs-12 rounded-1 edit ${classNamesToAdd ?? ''}`}
			onClick={(e) => onClick && onClick(rowData, e)}
		>
			{labelTrans}
		</span>
	</>
}

export const OfferScopeListTable = (props: IOfferScopeListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onView,
		onBind,
		onUnbind,
		onEdit,
		onEditNotes,
		onRemove
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)
	//TODO: REMOVE CRUTCH - ACCESS FOR TEAM-X-USERS'
	const isTeam = useAtomValue(state.isTeamAtom)

	const offerScopeListTableConfig: IUiTableConfig<IOfferScope>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_id)
		},
		{
			key: 'name',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_name)
		},
		{
			key: 'stats.ids',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_offer_id),
			rowCellFormatter: ({ value, rowData }) => {
				const valueFormat: Array<string> = rowData?.stats?.ids?.map(item => String(item)) ?? []

				return <UiMoreTextInfo value={valueFormat}/>
			}
		},
		//TODO: check if group column is necessary
		/*{
			key: 'group',
			title: 'Group'
		},*/
		{
			key: 'notes',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_notes),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			//TODO: check if OfferScopeNotesField with pop-up is necessary
			/*rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const notesProps = { value, rowData, isViewMode: isTeam }
				const rowsLength = rowsData.length
				let popUpPosition: IPopUpPosition

				if ([0, 1, 2].includes(rowIndex)) {
					popUpPosition = 'top'
				} else if ([rowsLength - 1, rowsLength - 2, rowsLength - 3].includes(rowIndex)) {
					popUpPosition = 'bottom'
				} else {
					popUpPosition = 'middle'
				}

				return <OfferScopeNotesField {...notesProps} popUpPosition={popUpPosition}/>
			}*/
			rowCellFormatter: ({ value, rowData }) => {
				const val = rowData?.notes

				return <>
					{
						onEditNotes && !!$access.$(ERoleAccess.OfferScopeList)
							? (
								<NotesIconBtn value={val}
								              onClick={(e) => onEditNotes && onEditNotes(rowData, e)}
								/>
							)
							: null
					}
				</>
			}
		},
		{
			key: 'affiliateNetwork',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_affiliate_network),
			rowCellFormatter: ({ value, rowData }) => {
				const valueFormat: Array<string> = value ?? []

				return <UiMoreTextInfo value={valueFormat}/>
			}
		},
		{
			key: 'country',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_country),
			rowCellFormatter: ({ value, rowData }) => {
				const valueFormat: Array<string> = value ?? []

				return <UiMoreTextInfo value={valueFormat}/>
			}
		},
		{
			key: 'sales/cap',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_sales_cap),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				return <div>
					{Number(rowData?.sales).toFixed(0)}/{Number(rowData?.cap).toFixed(0)}
				</div>
			}
		},
		{
			key: 'diff',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_diff),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			}
		},
		{
			key: 'capType',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_cap_type),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				const valueFormat: EOfferScopeCapType = value
				const keyTrans = OFFER_SCOPE_CAP_TYPE_LABEL_MAP?.[valueFormat]
				const labelTrans = t(keyTrans)

				return (
					<div title={labelTrans}>
						<CapTypeIcon value={value}/>
					</div>
				)
			}
		},
		{
			key: 'status',
			title: t(ALL_LANG_KEY.offer_scope_list_table.column_status),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				//TODO: formatter for 'status' field in edit mode (dropdown select...?)
				const valueFormat: EOfferScopeStatus = value ?? EOfferScopeStatus.none
				return <div>
					<OfferScopeStatusField
						value={valueFormat}
						rowData={rowData}
						classNamesToAdd={STATUS_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on Status field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</div>
			}
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>
						{
							!!onView && !!$access.$(ERoleAccess.OfferList) && !$access.$(ERoleAccess.OfferUnbind) // !!! && !!isTeam.length
								? (
									<button name="view"
									        className="icon border-0 rounded-circle text-center bg-primary-transparent view-item"
									        onClick={(e) => onView && onView(rowData, e)}
									>
										<IconView/>
									</button>
								)
								: null
						}

						{
							!!onBind && !!$access.$(ERoleAccess.OfferBind) // !!! && !isTeam.length
								? (
									<Button
										className="icon"
										variant="outline-primary"
										style={{
											borderRadius: '100%',
											aspectRatio: 1,
											padding: '5px 10px'
										}}
										onClick={(e) => onBind && onBind(rowData, e)}
									>
										<FontAwesomeIcon icon={faLink} style={{ fontSize: '14px' }}/>
									</Button>
								)
								: null
						}

						{
							!!onUnbind && !!$access.$(ERoleAccess.OfferUnbind) // !!! && !isTeam.length
								? (
									<Button
										className="icon"
										variant="outline-primary"
										style={{
											borderRadius: '100%',
											aspectRatio: 1,
											padding: '5px 10px'
										}}
										onClick={(e) => onUnbind && onUnbind(rowData, e)}
									>
										<FontAwesomeIcon icon={faLinkSlash} style={{ fontSize: '14px' }}/>
									</Button>
								)
								: null
						}

						{
							!!onEdit && !!$access.$(ERoleAccess.OfferScopeModify) // !!! && !isTeam.length
								? (
									<button name="edit"
									        className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
									        onClick={(e) => onEdit && onEdit(rowData, e)}
									>
										<IconEdit/>
									</button>
								)
								: null
						}

						{
							!!onRemove && !!$access.$(ERoleAccess.OfferScopeRemove) // !!! && !isTeam.length
								? (
									<button
										name="delete"
										className="icon border-0 rounded-circle text-center trash bg-danger-transparent delete-item"
										onClick={(e) => onRemove && onRemove(rowData, e)}
									>
										<IconDeleteBin/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const offerScopeListTableConfigSortable: IUiTableConfig<IOfferScope>[] = offerScopeListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = OFFER_SCOPE_PAGE_SORT_FIELDS.includes(configItem.key as IOfferScopeSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('OfferScopeListTable:: ', { offerScopeListTableConfig, offerScopeListTableConfigSortable })

	return <>
		<UiTable
			data={items}
			config={offerScopeListTableConfigSortable}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}